import { useState, useEffect, useRef } from 'react';
import {
  Card, Select, Group, Table, SegmentedControl, Loader, Center, Text, Badge, TextInput, Alert, Button, Stack, Tabs,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { IconCheck, IconSearch, IconCalendarCheck, IconAlertTriangle, IconRefresh, IconInfoCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { useClasses } from '../hooks/useLookups';
import { FetchOverlay } from '../components/FetchOverlay';
import { TablePagination } from '../components/TablePagination';
import { PageHeader } from '../components/PageHeader';
import { StaffAttendance } from '../components/StaffAttendance';
import { SectionCard } from '../components/SectionCard';
import { ServerSearchSelect } from '../components/ServerSearchSelect';
import { TableToolbar } from '../components/TableToolbar';
import { formatDate } from '../lib/format';
import { SILENT_ERROR } from '../lib/queryMeta';

interface Student { id: string; fullName: string }
interface AttRecord { id: string; studentId: string; status: string }
interface AttFull { id: string; date: string; status: string; student?: { id: string; fullName: string } | null; createdById?: string | null; createdByName?: string | null }
interface Marker { id: string; fullName: string }

// Holat → badge rangi (Mantine)
const ATT_COLOR: Record<string, string> = { PRESENT: 'green', ABSENT: 'red', LATE: 'yellow', EXCUSED: 'blue' };

/**
 * Davomat — har bir o'quvchi ALOHIDA, status o'zgartirilishi bilan
 * avtomatik saqlanadi (saqlash tugmasi yo'q).
 */
export default function Attendance() {
  const { t } = useTranslation();
  const STATUS_OPTIONS = [
    { label: t('att.PRESENT'), value: 'PRESENT' },
    { label: t('att.ABSENT'), value: 'ABSENT' },
    { label: t('att.LATE'), value: 'LATE' },
    { label: t('att.EXCUSED'), value: 'EXCUSED' },
  ];
  const [mode, setMode] = useState<'mark' | 'view'>('mark'); // Belgilash / Ko'rish
  /* Yuz tanish terminali ishlamay qolganda xodim davomati ham SHU sahifadan
     olinadi — admin ikki joyni izlab yurmasin. */
  const [kim, setKim] = useState<string | null>('students');
  const [classId, setClassId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(new Date());
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());
  const savedTimer = useRef<Record<string, number>>({});
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());

  const { data: classes } = useClasses();

  const [search, setSearch] = useState('');
  const [qDeb] = useDebouncedValue(search, 300);
  const [statusF, setStatusF] = useState<string | null>(null); // holat bo'yicha filtr
  const [sortBy, setSortBy] = useState<'name' | 'status'>('name'); // saralash (belgilash rejimi)

  // Ko'rish rejimi — sana oralig'i (default: oxirgi 30 kun) + server saralash
  const [range, setRange] = useState<[Date | null, Date | null]>(() => {
    const to = new Date();
    const fromD = new Date();
    fromD.setDate(fromD.getDate() - 29);
    return [fromD, to];
  });
  const [viewSort, setViewSort] = useState('date:desc');
  const [viewStudentId, setViewStudentId] = useState<string | null>(null); // bitta o'quvchi tarixi
  const [markerF, setMarkerF] = useState<string | null>(null); // "kim oldi" filtri
  // Ko'rish rejimi qidiruvi — belgilash rejimidagi `search` dan ALOHIDA (u sinf o'quvchilarini filtrlaydi)
  const [viewSearch, setViewSearch] = useState('');
  const [viewQDeb] = useDebouncedValue(viewSearch, 300);
  const { data: markers } = useQuery({
    queryKey: ['att-markers'],
    queryFn: async () => (await api.get<Marker[]>('/attendance/markers')).data,
    enabled: mode === 'view',
  });
  // Lokal sana → YYYY-MM-DD. toISOString() emas — UTC+5 da lokal yarim tun
  // bir kun orqaga siljib, noto'g'ri oraliq so'ralardi.
  const iso = (d: Date | null) =>
    d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;
  const { items: records, isLoading: recLoading, fetching, total, pages, page, setPage, limit, setLimit} = usePagedQuery<AttFull>(
    'attendance-view',
    '/attendance',
    {
      q: viewQDeb || undefined,
      classId: classId || undefined, studentId: viewStudentId || undefined, createdById: markerF || undefined,
      from: iso(range[0]), to: iso(range[1]), status: statusF || undefined, sort: viewSort,
    },
    20,
    mode === 'view', // Belgilash rejimida bu so'rov keraksiz — yuborilmaydi
  );
  const { data: students, isLoading, isError: studentsError, refetch: refetchStudents } = useQuery({
    queryKey: ['students-of-class', classId, qDeb],
    queryFn: async () => (await api.get<Student[]>('/students', { params: { classId, q: qDeb || undefined } })).data,
    enabled: !!classId,
    meta: SILENT_ERROR, // xato ro'yxat o'rnida ochiq ko'rsatiladi
  });

  // Shu kun uchun mavjud davomatni oldindan yuklash.
  // MUHIM: lokal (Toshkent) sana — toISOString() UTC'ga o'tkazib kunni siljitardi.
  const dateKey = iso(date) ?? '';
  const {
    data: existing, isPending: existingPending, isError: existingError,
    isFetching: existingFetching, refetch: refetchExisting,
  } = useQuery({
    queryKey: ['attendance-existing', classId, dateKey],
    queryFn: async () => (await api.get<AttRecord[]>('/attendance', { params: { classId, date: dateKey } })).data,
    enabled: !!classId && !!dateKey,
    meta: SILENT_ERROR, // xato ro'yxat o'rnida ochiq ko'rsatiladi
  });

  /*
   * XATO holat ochiq ko'rsatiladi — bu yerda "ma'lumot yo'q" bilan aralashish XAVFLI.
   * Quyidagi effekt avval HAMMANI `PRESENT` qilib, keyin serverdagi yozuvlar bilan
   * ustidan yozadi. Ilgari so'rov yiqilganda ham ro'yxat chizilardi va o'qituvchi
   * ABSENT/LATE belgilangan o'quvchilarni ham "Keldi" holatida ko'rardi — ustiga
   * bosilsa esa haqiqiy yozuv shu yolg'on qiymat bilan almashardi.
   */
  /* DAM OLISH KUNIMI — serverdan. Brauzerda hafta kunini sanash yetarli emas:
     maktab shanba ishlashi mumkin, ustiga bayram kunlari bor. Yagona manba
     serverda (`lib/workdays.ts`). */
  const { data: kunHolati } = useQuery({
    queryKey: ['attendance-day-info', dateKey],
    queryFn: async () =>
      (await api.get<{ ishKuni: boolean }>('/attendance/day-info', { params: { date: dateKey } })).data,
    enabled: !!dateKey,
  });
  const damOlish = kunHolati ? !kunHolati.ishKuni : false;

  const markError = studentsError || existingError;
  const retryMark = () => { void refetchStudents(); void refetchExisting(); };

  useEffect(() => {
    // Ikkala so'rov ham kelmaguncha `marks` to'ldirilmaydi (aks holda bir zumga
    // "hamma keldi" holati ko'rinardi); jadval ham shu paytgacha chizilmaydi.
    if (!students || !existing) return;
    const init: Record<string, string> = {};
    students.forEach((s) => (init[s.id] = 'PRESENT'));
    existing.forEach((r) => { if (init[r.studentId]) init[r.studentId] = r.status; });
    setMarks(init);
  }, [students, existing]);

  /** Bitta o'quvchini darhol saqlash. */
  const saveOne = async (studentId: string, status: string) => {
    const prev = marks[studentId];
    setMarks((m) => ({ ...m, [studentId]: status }));
    setSavingIds((s) => new Set(s).add(studentId));
    try {
      await api.post('/attendance/mark', {
        date: iso(date), // lokal (Toshkent) sana — server uni Toshkent kuni deb oladi
        records: [{ studentId, status }],
      });
      setSavedIds((s) => new Set(s).add(studentId));
      window.clearTimeout(savedTimer.current[studentId]);
      savedTimer.current[studentId] = window.setTimeout(() => {
        setSavedIds((s) => { const n = new Set(s); n.delete(studentId); return n; });
      }, 1600);
    } catch {
      setMarks((m) => ({ ...m, [studentId]: prev }));
      notifications.show({ color: 'red', message: t('attPage.saveError') });
    } finally {
      setSavingIds((s) => { const n = new Set(s); n.delete(studentId); return n; });
    }
  };

  // Holat bo'yicha filtr + saralash (belgilangan `marks` asosida, mijoz tomonda)
  const STATUS_ORDER = ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED'];
  const visible = (students ?? [])
    .filter((s) => !statusF || (marks[s.id] ?? 'PRESENT') === statusF)
    .slice()
    .sort((a, b) =>
      sortBy === 'status'
        ? STATUS_ORDER.indexOf(marks[a.id] ?? 'PRESENT') - STATUS_ORDER.indexOf(marks[b.id] ?? 'PRESENT') ||
          a.fullName.localeCompare(b.fullName)
        : a.fullName.localeCompare(b.fullName)
    );

  // Ko'rish rejimi filtrlari o'zgarsa — 1-sahifaga qaytish
  const resetPage = () => setPage(1);

  return (
    <>
      <Tabs value={kim} onChange={setKim} mb="md">
        <Tabs.List>
          <Tabs.Tab value="students">{t('attPage.tabStudents')}</Tabs.Tab>
          <Tabs.Tab value="staff">{t('attPage.tabStaff')}</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {kim === 'staff' ? <StaffAttendance /> : <>
      <PageHeader
        title={t('pages.attendance.title')}
        subtitle={mode === 'mark' ? t('att.markSubtitle') : t('att.viewSubtitle')}
        action={
          <SegmentedControl
            value={mode}
            onChange={(v) => { setMode(v as 'mark' | 'view'); resetPage(); }}
            data={[
              { value: 'mark', label: t('att.modeMark') },
              { value: 'view', label: t('att.modeView') },
            ]}
          />
        }
      />

      {mode === 'mark' ? (
        <>
          <Card mb="md">
            <Group>
              <Select
                label={t('fields.class')}
                placeholder={t('common.selectPlaceholder')}
                data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
                value={classId}
                onChange={(v) => { setClassId(v); resetPage(); }}
                searchable
                w={200}
              />
              <DateInput label={t('att.date')} value={date} onChange={setDate} valueFormat="DD.MM.YYYY" w={180} />
              <TextInput
                label={t('attPage.searchStudent')}
                placeholder={t('attPage.namePlaceholder')}
                leftSection={<IconSearch size={14} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                w={200}
              />
              <Select
                label={t('att.filterStatus')}
                placeholder={t('att.allStatuses')}
                data={STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(`att.${o.value}`) }))}
                value={statusF}
                onChange={(v) => { setStatusF(v); resetPage(); }}
                clearable
                w={160}
              />
              <Select
                label={t('att.sortBy')}
                data={[
                  { value: 'name', label: t('att.sortName') },
                  { value: 'status', label: t('att.sortStatus') },
                ]}
                value={sortBy}
                onChange={(v) => setSortBy((v as 'name' | 'status') ?? 'name')}
                allowDeselect={false}
                w={170}
              />
            </Group>
          </Card>

          {/* Sarlavhali karta DOIM chiziladi — ichida tanlov/yuklanish/jadval almashadi
              (shunda "yuklanmoqda → bo'sh → to'la" o'tishida sahifa sakramaydi) */}
          <SectionCard title={t('common.list')} icon={IconCalendarCheck} p={0}>
            {!classId ? (
              <Center h={140}><Text c="dimmed">{t('attPage.selectClassToMark')}</Text></Center>
            ) : !dateKey ? (
              <Center h={140}><Text c="dimmed">{t('attPage.selectDateToMark')}</Text></Center>
            ) : markError ? (
              // Yolg'on "hamma keldi" ro'yxati o'rniga ochiq xato + qayta urinish
              <Stack align="center" gap="sm" p="lg">
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
                  {t('attPage.loadError')}
                </Alert>
                <Button variant="light" loading={isLoading || existingFetching} leftSection={<IconRefresh size={16} />} onClick={retryMark}>
                  {t('attPage.retry')}
                </Button>
              </Stack>
            ) : damOlish ? (
              /* Dam olish kunida jadval UMUMAN chizilmaydi: ro'yxatni ko'rsatib
                 turib har bosishda xato qaytarish chalg'itardi. */
              <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />} m="md">
                {t('attPage.restDay')}
              </Alert>
            ) : isLoading || existingPending ? (
              <Center h={160}><Loader /></Center>
            ) : !students?.length ? (
              <Center h={140}><Text c="dimmed">{t('attPage.noStudentsInClass')}</Text></Center>
            ) : (
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('att.student')}</Table.Th>
                    <Table.Th>{t('att.status')}</Table.Th>
                    <Table.Th w={90}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {!visible.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={3}>
                        <Text c="dimmed" ta="center" py="md">{t('att.noMatch')}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : null}
                  {visible.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td fw={500}>{s.fullName}</Table.Td>
                      <Table.Td>
                        <SegmentedControl
                          size="xs"
                          data={STATUS_OPTIONS}
                          value={marks[s.id] ?? 'PRESENT'}
                          onChange={(val) => saveOne(s.id, val)}
                          disabled={savingIds.has(s.id)}
                        />
                      </Table.Td>
                      <Table.Td>
                        {savingIds.has(s.id) ? (
                          <Loader size={16} />
                        ) : savedIds.has(s.id) ? (
                          <Badge size="sm" color="teal" leftSection={<IconCheck size={12} />}>{t('attPage.saved')}</Badge>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </SectionCard>
        </>
      ) : (
        <>
          <TableToolbar
            search={viewSearch}
            onSearch={setViewSearch}
            searchPlaceholder={t('attPage.namePlaceholder')}
            sort={viewSort}
            onSort={(v) => { setViewSort(v); resetPage(); }}
            sortOptions={[
              { value: 'date:desc', label: t('att.sortDateDesc') },
              { value: 'date:asc', label: t('att.sortDateAsc') },
              { value: 'status:asc', label: t('att.sortStatus') },
              { value: 'createdById:asc', label: t('att.sortMarker') },
            ]}
          >
            <Select
              placeholder={t('att.allClasses')}
              data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={classId || null}
              onChange={(v) => { setClassId(v); resetPage(); }}
              searchable
              clearable
              w={170}
            />
            <ServerSearchSelect
              placeholder={t('att.allStudents')}
              endpoint="/students"
              mapOption={(st: { id: string; fullName: string; class?: { name: string } | null }) =>
                ({ value: st.id, label: `${st.fullName}${st.class ? ` · ${st.class.name}` : ''}` })}
              value={viewStudentId || null}
              onChange={(v) => { setViewStudentId(v); resetPage(); }}
              clearable
              w={190}
            />
            <DatePickerInput
              type="range"
              placeholder={t('att.dateRange')}
              value={range}
              onChange={(v) => { setRange(v); resetPage(); }}
              valueFormat="DD.MM.YYYY"
              w={190}
              clearable
            />
            <Select
              placeholder={t('att.allStatuses')}
              data={STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(`att.${o.value}`) }))}
              value={statusF || null}
              onChange={(v) => { setStatusF(v); resetPage(); }}
              clearable
              w={150}
            />
            <Select
              placeholder={t('att.allMarkers')}
              data={(markers ?? []).map((m) => ({ value: m.id, label: m.fullName }))}
              value={markerF || null}
              onChange={(v) => { setMarkerF(v); resetPage(); }}
              clearable searchable
              w={170}
            />
          </TableToolbar>

          {!recLoading && !!records.length && (
            <Text size="sm" c="dimmed" mb="xs">{t('att.total')}: {total}</Text>
          )}
          {/* Karta doim ko'rinadi — bo'sh/yuklanmoqda holati ham sarlavha ostida */}
          <SectionCard title={t('common.list')} icon={IconCalendarCheck} p={0} pos="relative">
            <FetchOverlay visible={fetching} />
            {recLoading ? (
              <Center h={160}><Loader /></Center>
            ) : !records.length ? (
              <Center h={140}><Text c="dimmed">{t('att.noRecords')}</Text></Center>
            ) : (
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('att.student')}</Table.Th>
                    <Table.Th>{t('att.date')}</Table.Th>
                    <Table.Th>{t('att.status')}</Table.Th>
                    <Table.Th>{t('att.markedBy')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {records.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td fw={500}>{r.student?.fullName ?? '—'}</Table.Td>
                      <Table.Td>{formatDate(r.date)}</Table.Td>
                      <Table.Td>
                        <Badge color={ATT_COLOR[r.status] ?? 'gray'} variant="light">{t(`att.${r.status}`)}</Badge>
                      </Table.Td>
                      <Table.Td>{r.createdByName ?? <Text c="dimmed" size="sm">—</Text>}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </SectionCard>
          <TablePagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPage={setPage}
            onLimit={setLimit}
          />
        </>
      )}
    </>}
    </>
  );
}
